/**
 * Cross-browser clipboard copy with iOS Safari fallback.
 *
 * `navigator.clipboard.writeText` is unreliable on iOS (especially inside async
 * handlers), so we fall back to a temporary textarea + `document.execCommand`.
 */

function isIOS(): boolean {
	if (typeof navigator === "undefined") return false;
	return (
		/iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
	);
}

/** iOS Safari must copy synchronously inside the tap handler — async clipboard API loses user activation. */
function prefersSyncLegacyCopy(): boolean {
	return isIOS();
}

function legacyCopyToClipboard(text: string): void {
	const textarea = document.createElement("textarea");
	textarea.value = text;
	const ios = isIOS();

	// iOS ignores copy on readonly/off-screen fields; keep a tiny in-viewport target.
	if (ios) {
		textarea.contentEditable = "true";
		textarea.readOnly = false;
	} else {
		textarea.setAttribute("readonly", "");
	}

	textarea.style.position = "fixed";
	textarea.style.top = "0";
	textarea.style.left = "0";
	textarea.style.width = "2em";
	textarea.style.height = "2em";
	textarea.style.padding = "0";
	textarea.style.border = "none";
	textarea.style.outline = "none";
	textarea.style.boxShadow = "none";
	textarea.style.background = "transparent";
	textarea.style.fontSize = "16px";
	textarea.style.opacity = "0";
	textarea.style.pointerEvents = "none";
	textarea.style.zIndex = "-1";

	document.body.appendChild(textarea);

	const selection = document.getSelection();
	const selectedRange =
		selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

	textarea.focus();

	if (ios) {
		const range = document.createRange();
		range.selectNodeContents(textarea);
		selection?.removeAllRanges();
		selection?.addRange(range);
		textarea.setSelectionRange(0, text.length);
	} else {
		textarea.select();
	}

	let copied = false;
	try {
		copied = document.execCommand("copy");
	} finally {
		document.body.removeChild(textarea);
		if (selectedRange && selection) {
			selection.removeAllRanges();
			selection.addRange(selectedRange);
		}
	}

	if (!copied) {
		throw new Error("Copy command was not successful");
	}
}

/**
 * Copy text to the system clipboard.
 * @throws when copy fails in both modern and legacy paths.
 */
export async function copyToClipboard(text: string): Promise<void> {
	if (prefersSyncLegacyCopy()) {
		legacyCopyToClipboard(text);
		return;
	}

	if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return;
		} catch {
			// Fall through to legacy copy when permission or secure context blocks the API.
		}
	}

	legacyCopyToClipboard(text);
}
